
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CourseModule, CourseLesson } from "@/types/course";
import { toast } from "@/hooks/use-toast";
import { createModule, updateModule, deleteModule, reorderModules, getCourseById } from "@/services/courseManagementService";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  BreadcrumbPage
} from "@/components/ui/breadcrumb";

const ModulesManagementPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentModule, setCurrentModule] = useState<{
    id?: string;
    title: string;
    description: string;
  }>({
    title: "",
    description: "",
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);
  const [courseTitle, setCourseTitle] = useState<string>("");
  
  useEffect(() => {
    if (!courseId) {
      navigate("/admin/courses");
      return;
    }
    
    fetchCourseInfo();
    fetchModules();
  }, [courseId, navigate]);

  const fetchCourseInfo = async () => {
    if (!courseId) return;
    
    try {
      const course = await getCourseById(courseId);
      if (course) {
        setCourseTitle(course.title);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Course not found.",
        });
        navigate("/admin/courses");
      }
    } catch (error) {
      console.error("Error fetching course info:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch course information.",
      });
    }
  };

  const fetchModules = async () => {
    if (!courseId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('course_modules')
        .select(`
          *,
          course_lessons (id, title, description, video_url, order_index, duration)
        `)
        .eq('course_id', courseId)
        .order('order_index');
        
      if (error) throw error;
      
      // Properly map the data to CourseModule type
      const processedModules: CourseModule[] = data.map(module => ({
        ...module,
        lessons: (module.course_lessons || []).map(lesson => ({
          id: lesson.id,
          module_id: module.id,
          title: lesson.title,
          description: lesson.description,
          video_url: lesson.video_url,
          order_index: lesson.order_index,
          duration: lesson.duration,
          completed: false, // Default value
          last_position: 0 // Default value
        })),
        completedLessons: 0,
        totalLessons: module.course_lessons ? module.course_lessons.length : 0
      }));
      
      setModules(processedModules);
    } catch (error) {
      console.error("Error fetching modules:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch modules. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateModule = async () => {
    try {
      if (!courseId || !currentModule.title.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Module title is required.",
        });
        return;
      }

      const newOrderIndex = modules.length > 0 
        ? Math.max(...modules.map(m => m.order_index)) + 1 
        : 0;
      
      await createModule(
        courseId,
        currentModule.title,
        currentModule.description || null,
        newOrderIndex
      );
      
      toast({
        title: "Success",
        description: "Module created successfully.",
      });
      
      setIsCreating(false);
      setCurrentModule({ title: "", description: "" });
      fetchModules();
    } catch (error) {
      console.error("Error creating module:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create module. Please try again.",
      });
    }
  };

  const handleEditModule = async () => {
    try {
      if (!currentModule.id || !currentModule.title.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Module title is required.",
        });
        return;
      }

      await updateModule(currentModule.id, {
        title: currentModule.title,
        description: currentModule.description || null,
      });
      
      toast({
        title: "Success",
        description: "Module updated successfully.",
      });
      
      setIsEditing(false);
      setCurrentModule({ title: "", description: "" });
      fetchModules();
    } catch (error) {
      console.error("Error updating module:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update module. Please try again.",
      });
    }
  };

  const handleDeleteModule = async () => {
    try {
      if (!moduleToDelete) return;

      await deleteModule(moduleToDelete);
      
      toast({
        title: "Success",
        description: "Module deleted successfully.",
      });
      
      setIsDeleteDialogOpen(false);
      setModuleToDelete(null);
      fetchModules();
    } catch (error) {
      console.error("Error deleting module:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete module. Please try again.",
      });
    }
  };

  const handleMoveModule = async (moduleId: string, direction: "up" | "down") => {
    const moduleIndex = modules.findIndex(m => m.id === moduleId);
    if (
      (direction === "up" && moduleIndex === 0) || 
      (direction === "down" && moduleIndex === modules.length - 1)
    ) {
      return;
    }

    const newModules = [...modules];
    const targetIndex = direction === "up" ? moduleIndex - 1 : moduleIndex + 1;
    
    // Swap modules
    [newModules[moduleIndex], newModules[targetIndex]] = [
      newModules[targetIndex],
      newModules[moduleIndex],
    ];
    
    try {
      // Fixed: Ensure courseId is passed as the first parameter
      await reorderModules(courseId, newModules.map(m => m.id));
      setModules(newModules);
      
      toast({
        title: "Success",
        description: "Module order updated.",
      });
    } catch (error) {
      console.error("Error reordering modules:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to reorder modules. Please try again.",
      });
    }
  };

  return (
    <AdminLayout 
      title={`Course: ${courseTitle}`}
      backLink="/admin/courses/list"
    >
      <div className="mb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/courses/list">Courses</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{courseTitle}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Modules</CardTitle>
              <CardDescription>
                Manage modules for this course. Click on a module to view and manage its lessons.
              </CardDescription>
            </div>
            <Button 
              onClick={() => {
                setCurrentModule({ title: "", description: "" });
                setIsCreating(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" /> Add Module
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p>Loading modules...</p>
            </div>
          ) : modules.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No modules found. Create your first module to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Order</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-[100px]">Lessons</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modules.map((module) => (
                  <TableRow 
                    key={module.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/admin/courses/modules/${courseId}/${module.id}/lessons`)}
                  >
                    <TableCell className="font-medium">{module.order_index + 1}</TableCell>
                    <TableCell>{module.title}</TableCell>
                    <TableCell>{module.description || "-"}</TableCell>
                    <TableCell>{module.lessons?.length || 0}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentModule({
                              id: module.id,
                              title: module.title,
                              description: module.description || "",
                            });
                            setIsEditing(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setModuleToDelete(module.id);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          disabled={modules.indexOf(module) === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveModule(module.id, "up");
                          }}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon"
                          disabled={modules.indexOf(module) === modules.length - 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveModule(module.id, "down");
                          }}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Module Dialog */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Module</DialogTitle>
            <DialogDescription>
              Create a new module for your course.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="Enter module title"
                value={currentModule.title}
                onChange={(e) => 
                  setCurrentModule({ ...currentModule, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter module description"
                value={currentModule.description}
                onChange={(e) => 
                  setCurrentModule({ ...currentModule, description: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreating(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateModule}>Create Module</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Module Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Module</DialogTitle>
            <DialogDescription>
              Update module details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                placeholder="Enter module title"
                value={currentModule.title}
                onChange={(e) => 
                  setCurrentModule({ ...currentModule, title: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Enter module description"
                value={currentModule.description}
                onChange={(e) => 
                  setCurrentModule({ ...currentModule, description: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditModule}>Update Module</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this module? This action cannot be undone.
              All lessons and content within this module will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteModule}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ModulesManagementPage;
